import { type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../../lib/utils';

import { searchInputVariants } from './variants';

export interface SearchInputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof searchInputVariants> {
  onClear?: () => void;
  showClearButton?: boolean;
  searchIcon?: React.ReactNode;
  errorIcon?: React.ReactNode;
  clearIcon?: React.ReactNode;
  'data-testid'?: string;
}

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      className,
      state,
      size,
      onClear,
      showClearButton = false,
      searchIcon,
      errorIcon,
      clearIcon,
      value,
      'data-testid': dataTestId,
      ...props
    },
    ref,
  ) => {
    const shouldShowClear = showClearButton && state !== 'disabled';
    const isError = state === 'error';

    return (
      <div className={cn(searchInputVariants({ state, size }), className)}>
        {/* Search Icon or Error Icon */}
        {(isError ? errorIcon : searchIcon) && (
          <div className="flex-shrink-0 w-[1.125rem] h-[1.125rem] flex items-center justify-center">
            {isError ? errorIcon : searchIcon}
          </div>
        )}

        {/* Input Field */}
        <input
          className="min-w-0 flex-1 bg-transparent border-none outline-none placeholder:text-inherit text-inherit disabled:cursor-not-allowed"
          ref={ref}
          value={value}
          disabled={state === 'disabled'}
          data-testid={dataTestId}
          {...props}
        />

        {/* Clear Button */}
        {shouldShowClear && (
          <button
            type="button"
            onClick={onClear}
            className="flex-shrink-0 w-[1.125rem] h-[1.125rem] flex items-center justify-center hover:opacity-70 transition-opacity"
            aria-label="Clear search"
            data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
          >
            {clearIcon}
          </button>
        )}
      </div>
    );
  },
);
SearchInput.displayName = 'SearchInput';

export { SearchInput };
